import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Wallet, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import playerService from '../../services/playerService';
import toast from 'react-hot-toast';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
`;

const PlayerInfo = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
`;

const PlayerName = styled.div`
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const PlayerBalance = styled.div`
  color: #4ecdc4;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormContainer = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  overflow: hidden;
`;

const AmountInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  padding: 12px 15px;
  color: #ffffff;
  font-size: 16px;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: #6c6c8a;
  }
`;

const CurrencyText = styled.div`
  background: #2d2d44;
  color: #b8b8d1;
  padding: 12px 15px;
  font-size: 14px;
  font-weight: 500;
  border-left: 1px solid #2d2d44;
`;

const TextArea = styled.textarea`
  width: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 12px 15px;
  color: #ffffff;
  font-size: 16px;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
  
  &::placeholder {
    color: #6c6c8a;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin: 20px 0;
  padding: 15px;
  background: linear-gradient(90deg, rgba(255, 107, 107, 0.1) 0%, transparent 100%);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: #b8b8d1;
`;

const ChargeWalletModal = ({ 
  show, 
  player, 
  onClose, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeDescription, setChargeDescription] = useState('');
  const [error, setError] = useState('');

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (show) {
      setChargeAmount('');
      setChargeDescription('');
      setError('');
    }
  }, [show]);

  const handleChargeWallet = async () => {
    if (!player || !chargeAmount || !chargeDescription) {
      setError('لطفاً تمام فیلدها را پر کنید');
      return;
    }

    const amount = parseFloat(chargeAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      setError('مبلغ باید عدد مثبت باشد');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await playerService.chargeWallet({
        PlayerId: player.id,
        Amount: amount,
        Description: chargeDescription
      });

      toast.success('کیف پول با موفقیت شارژ شد');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(`خطا در شارژ کیف پول: ${err.message || 'خطای نامشخص'}`);
      console.error('Error charging wallet:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!show || !player) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Wallet size={24} />
            شارژ کیف پول بازیکن
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <PlayerInfo>
            <PlayerName>
              <strong>بازیکن:</strong> {player.username}
            </PlayerName>
            <PlayerBalance>
              <Wallet size={16} />
              <strong>موجودی فعلی:</strong> {player.balance || 0} تومان
            </PlayerBalance>
          </PlayerInfo>

          <FormContainer>
            <FormGroup>
              <Label>مبلغ شارژ</Label>
              <InputGroup>
                <AmountInput
                  type="number"
                  placeholder="مبلغ را وارد کنید"
                  value={chargeAmount}
                  onChange={(e) => setChargeAmount(e.target.value)}
                  min="0.01"
                  step="0.01"
                  disabled={loading}
                />
                <CurrencyText>تومان</CurrencyText>
              </InputGroup>
            </FormGroup>
            
            <FormGroup>
              <Label>توضیحات</Label>
              <TextArea
                placeholder="توضیحات شارژ (اختیاری)"
                value={chargeDescription}
                onChange={(e) => setChargeDescription(e.target.value)}
                disabled={loading}
                rows={3}
              />
            </FormGroup>

            {error && (
              <ErrorMessage>
                <AlertCircle size={16} />
                {error}
              </ErrorMessage>
            )}

            <ActionButtons>
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={loading}
                icon={<X size={16} />}
              >
                انصراف
              </Button>
              <Button 
                type="button" 
                variant="primary" 
                onClick={handleChargeWallet}
                disabled={loading || !chargeAmount || !chargeDescription}
                icon={<CheckCircle size={16} />}
              >
                {loading ? 'در حال شارژ...' : 'شارژ کیف پول'}
              </Button>
            </ActionButtons>
          </FormContainer>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ChargeWalletModal;
